import React from "react";
import { getCloudinaryUrl } from "./Image";

export const IMG_TAG_START_LENGTH = 6;
export const IMG_TAG_END_LENGTH = 2;
// Appending '//' to use regex
export const BOLD_OPEN_TAG = "\\[\\["; // BOLD_OPEN_TAG is [[
export const BOLD_CLOSE_TAG = "\\]\\]"; // BOLD_CLOSE_TAG is ]]
export const NEW_LINE_TAG = "\\\n"; // NEW_LINE_TAG is \n
export const IMAGE_OPEN_TAG = "{{img|";
export const IMAGE_CLOSE_TAG = "}}";
export const SAVINGS_OPEN_TAG = "\\(\\(savings\\|";
export const SAVINGS_CLOSE_TAG = "\\)\\)";

// used in SavingsLineItem.tsx to handle swiggy one savings
export const SWIGGY_ONE_SAVINGS_OPEN_TAG = "\\(\\(savings-one\\|";
export const SWIGGY_ONE_SAVINGS_CLOSE_TAG = "\\)\\)";

// used in SavingsLineItem.tsx to handle strikethrough
const STRIKETHROUGH_OPEN_TAG = "\\(\\(strikethrough\\|";
const STRIKETHROUGH_CLOSE_TAG = "\\)\\)";

export const HIGHLIGHTED_SAVINGS_TEXT_COLOR = "#1BA672";
export const HIGHLIGHTED_SWIGGY_ONE_SAVINGS_TEXT_COLOR = "#F15C52";

// used in paymentMethodName.tsx to handle color text
export const COLOR_OPEN_TAG = "\\(\\(color\\|";
export const COLOR_CLOSE_TAG = "\\)\\)";
export const COLOR_REGEX = `${COLOR_OPEN_TAG}([^|]*)\\|([^|]*)\\|([\\s\\S]*?)${COLOR_CLOSE_TAG}`;

const imageConfigFormatter = (imgString: string) => {
    // width|height|cloudinaryId
    // width and height need to be "__px" strings
    const [width, height, cloudinaryIdPostText] = imgString.split("|");
    const cloudinaryId = cloudinaryIdPostText?.replace(IMAGE_OPEN_TAG, "").replace(IMAGE_CLOSE_TAG, "");
    return {
        width,
        height,
        cloudinaryId,
    };
};

export const extractImageConfigFromString = (imgString: string) => {
    if (imgString.includes(IMAGE_OPEN_TAG) && imgString.includes(IMAGE_CLOSE_TAG)) {
        return imageConfigFormatter(imgString.replace(IMAGE_OPEN_TAG, "").replace(IMAGE_CLOSE_TAG, ""));
    }
};

export class FormatTextToHtml {
    value: string;

    constructor(value: string) {
        this.value = value;
    }

    get val(): string {
        return this.value;
    }

    getBoldText = (): FormatTextToHtml => {
        this.value = this.value
            .replace(new RegExp(BOLD_OPEN_TAG, "g"), '<span style="font-weight: 600;">')
            .replace(new RegExp(BOLD_CLOSE_TAG, "g"), "</span>");
        return this;
    };

    // Format: ((color|colorCode|fontWeight|text))
    // Example: ((color|#FF0000|600|This is red bold text))
    // Parameters:
    // - colorCode: Hex color code (e.g. #FF0000)
    // - fontWeight:  font weight (e.g. 600)
    // - text: The text to be colored
    getColorText = (): FormatTextToHtml => {
        this.value = this.value.replace(new RegExp(COLOR_REGEX, "g"), (match, color, fontWeight, content) => {
            if (content.includes(COLOR_OPEN_TAG)) {
                return match;
            }

            const styles = [
                color.trim() ? `color: ${color}` : "",
                fontWeight.trim() ? `font-weight: ${fontWeight}` : "",
            ]
                .filter(Boolean)
                .join(";");

            return styles ? `<span style="${styles}">${content}</span>` : content;
        });
        return this;
    };

    getSavingsHighlightedText = (): FormatTextToHtml => {
        this.value = this.value
            .replace(
                new RegExp(SAVINGS_OPEN_TAG, "g"),
                `<span style='color: ${HIGHLIGHTED_SAVINGS_TEXT_COLOR};font-weight: 600;'>`,
            )
            .replace(new RegExp(SAVINGS_CLOSE_TAG, "g"), "</span>");

        return this;
    };

    getSwiggyOneSavingsHighlightedText = (): FormatTextToHtml => {
        this.value = this.value
            .replace(
                new RegExp(SWIGGY_ONE_SAVINGS_OPEN_TAG, "g"),
                `<span style='color: ${HIGHLIGHTED_SWIGGY_ONE_SAVINGS_TEXT_COLOR};font-weight: 600;'>`,
            )
            .replace(new RegExp(SWIGGY_ONE_SAVINGS_CLOSE_TAG, "g"), "</span>");

        return this;
    };

    getStrikethroughText = (): FormatTextToHtml => {
        this.value = this.value
            .replace(new RegExp(STRIKETHROUGH_OPEN_TAG, "g"), `<span style='text-decoration: line-through;'>`)
            .replace(new RegExp(STRIKETHROUGH_CLOSE_TAG, "g"), "</span>");

        return this;
    };

    getNewLine = (): FormatTextToHtml => {
        this.value = this.value.replace(new RegExp(NEW_LINE_TAG, "g"), "<br />");
        return this;
    };

    getImage = ({
        imgStyle = "",
        imgInlineStyles = {},
        imgHeight = "",
        imgWidth = "",
        loading = "eager",
        iconHasHeightWidth = false,
    }): FormatTextToHtml => {
        // Continue processing until no more image tags are found
        while (this.value.indexOf(IMAGE_OPEN_TAG) >= 0) {
            const imgTagStart = this.value.indexOf(IMAGE_OPEN_TAG);
            if (imgTagStart < 0) {
                // No more image tags found, break the loop
                break;
            }

            const imgTagEnd = this.value.indexOf(IMAGE_CLOSE_TAG, imgTagStart);
            if (imgTagEnd < 0) {
                // Malformed tag, break to avoid infinite loop
                break;
            }

            const logoUrl = this.value.slice(imgTagStart + IMG_TAG_START_LENGTH, imgTagEnd);
            let cloudinaryUrl = getCloudinaryUrl(logoUrl);
            let currentImgWidth = imgWidth;
            let currentImgHeight = imgHeight;

            if (iconHasHeightWidth) {
                try {
                    const imageString = this.value.substring(imgTagStart, imgTagEnd + IMAGE_CLOSE_TAG.length);
                    const imageConfig = imageConfigFormatter(
                        imageString?.replace(IMAGE_OPEN_TAG, "").replace(IMAGE_CLOSE_TAG, ""),
                    );
                    currentImgWidth = imageConfig.width;
                    currentImgHeight = imageConfig.height;
                    cloudinaryUrl = getCloudinaryUrl(imageConfig.cloudinaryId);
                } catch (_) {
                    // no-op
                }
            }

            // Replace the current image tag with HTML img tag
            this.value = `${this.value.slice(
                0,
                imgTagStart,
            )}<img class="${imgStyle}" src="${cloudinaryUrl}" width="${currentImgWidth}" height="${currentImgHeight}" loading="${loading}" style="${imgInlineStyles}" />${this.value.slice(
                imgTagEnd + IMG_TAG_END_LENGTH,
            )}`;
        }

        return this;
    };
}

// This adds support for backend driven inline images
export const replaceText = (text: string, imgStyle?: string, iconHasHeightWidth = false): React.ReactNode => {
    text = new FormatTextToHtml(text)
        .getColorText()
        .getSwiggyOneSavingsHighlightedText()
        .getSavingsHighlightedText()
        .getStrikethroughText()
        .getBoldText()
        .getNewLine()
        .getImage({ imgStyle, iconHasHeightWidth }).val;

    return <div dangerouslySetInnerHTML={{ __html: text }} />;
};
